/**
 * Typed content registry — deterministic shift order for the three-story slice.
 * Shell loads packs only through this registry (never hardcodes scenario branches
 * in the adapter).
 *
 * The per-level DATA now lives in self-contained `content/levels/{slug}/level.json`
 * files; the loader (`content/levels/index.ts`) reconstructs the runtime objects.
 * This module keeps its stable public API and simply re-points at that loader.
 */
import type { EditGraph, EditOp } from "@desk/core";
import {
  PACK_REGISTRY,
  SHIFT_PACK_ORDER,
  type PackEntry,
  type PackId,
} from "./levels/index.js";

export type { PackId, PackEntry } from "./levels/index.js";
export {
  PACK_REGISTRY,
  SHIFT_PACK_ORDER,
  headlineFramingsForPackId,
  unlocksForPackId,
} from "./levels/index.js";

export function getPackEntry(id: string): PackEntry | undefined {
  return PACK_REGISTRY[id as PackId];
}

export function resolvePackIdFromQuery(
  search: string,
  fallback: PackId = SHIFT_PACK_ORDER[0]!,
): PackId {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get("scenario") ?? params.get("pack");
  if (raw && raw in PACK_REGISTRY) return raw as PackId;
  return fallback;
}

export function createGraphForPack(id: PackId, seed: number, ops: EditOp[] = []): EditGraph {
  return PACK_REGISTRY[id].createGraph(seed, ops);
}

export function listShiftPacks(): PackEntry[] {
  return SHIFT_PACK_ORDER.map((id) => PACK_REGISTRY[id]);
}

export {
  VET_DAY_PACK,
  DRAMATIC_CAT_PACK,
  OFFICE_SNACK_BANDIT_PACK,
  createVetDayGraph,
  createDramaticCatGraph,
  createOfficeSnackBanditGraph,
  dramaticCatFairOps,
  dramaticCatManipOps,
  officeSnackBanditFairOps,
  officeSnackBanditManipOps,
  VET_DAY_HYBRID_VARIANT_ID,
  DRAMATIC_CAT_HYBRID_VARIANT_ID,
  OFFICE_SNACK_BANDIT_HYBRID_VARIANT_ID,
  VET_DAY_VIDEO_ASSET_ID,
  DRAMATIC_CAT_VIDEO_ASSET_ID,
  OFFICE_SNACK_BANDIT_VIDEO_ASSET_ID,
} from "./levels/index.js";
